import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { setOnboarding } from "@/lib/api";

const CONTEXTS = [
  { id: "call_center", key: "workContextCallCenter" },
  { id: "hospitality", key: "workContextHospitality" },
  { id: "restaurant", key: "workContextRestaurant" },
  { id: "retail", key: "workContextRetail" },
  { id: "travel", key: "workContextTravel" },
  { id: "warehouse_logistics", key: "industryWarehouseLogistics" },
  { id: "healthcare_support", key: "industryHealthcareSupport" },
  { id: "construction_trades", key: "industryConstructionTrades" },
  { id: "landscaping", key: "industryLandscaping" },
  { id: "other", key: "workContextOther" },
];

export default function Onboarding() {
  const { tr } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [level, setLevel] = useState("basic");
  const [work, setWork] = useState(null);
  const [otherDesc, setOtherDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const buildWorkProfile = () => {
    if (!work) return null;
    const profile = { industry: work };
    if (work === "other") {
      const desc = otherDesc.trim().slice(0, 600);
      if (desc) profile.other_description = desc;
    }
    return profile;
  };

  const submit = async (overrides = null) => {
    setSubmitting(true);
    try {
      const profile = overrides?.work_profile !== undefined ? overrides.work_profile : buildWorkProfile();
      const ctx = overrides?.work_context !== undefined ? overrides.work_context : work;
      const u = await setOnboarding({
        level,
        work_context: ctx,
        work_profile: profile,
      });
      setUser(u);
      navigate("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell hideNav>
      <section className="px-5 pt-6 pb-10 space-y-6" data-testid="onboarding-screen">
        <h1 className="text-2xl font-bold" data-testid="onboarding-title">
          {tr("onboardingTitle")}
        </h1>

        <p
          className="text-[13px] text-[rgb(var(--wpe-slate))] leading-relaxed"
          data-testid="onboarding-real-situations-note"
        >
          {tr("onboardingRealSituationsNote")}
        </p>

        <div className="wpe-card space-y-3">
          <h2 className="text-sm font-semibold text-[rgb(var(--wpe-slate))]">
            {tr("onboardingLevel")}
          </h2>
          <LevelPick
            active={level === "basic"}
            title={tr("levelBasic")}
            hint={tr("levelBasicHint")}
            onClick={() => setLevel("basic")}
            testid="level-basic-btn"
          />
          <LevelPick
            active={level === "intermediate"}
            title={tr("levelInter")}
            hint={tr("levelInterHint")}
            onClick={() => setLevel("intermediate")}
            testid="level-intermediate-btn"
          />
          <LevelPick
            active={level === "advanced"}
            title={tr("levelAdvanced")}
            hint={tr("levelAdvancedHint")}
            onClick={() => setLevel("advanced")}
            testid="level-advanced-btn"
          />
        </div>

        <div className="wpe-card space-y-3">
          <h2 className="text-sm font-semibold text-[rgb(var(--wpe-slate))]">
            {tr("onboardingContext")}
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {CONTEXTS.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setWork(work === c.id ? null : c.id)}
                className={`py-2 px-3 rounded-lg border text-[13px] text-left transition ${
                  work === c.id
                    ? "border-[rgb(var(--wpe-teal))] bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]"
                    : "border-[rgb(var(--wpe-border))] bg-white text-[rgb(var(--wpe-slate))]"
                }`}
                data-testid={`work-context-${c.id}-btn`}
              >
                {tr(c.key)}
              </button>
            ))}
          </div>

          {work === "other" && (
            <div className="space-y-1 pt-1" data-testid="onboarding-other-desc-wrap">
              <label className="wpe-eyebrow block" htmlFor="onboarding-other-desc">
                {tr("otherJobDescribeLabel")}
              </label>
              <p className="text-[11px] text-[rgb(var(--wpe-muted))]">
                {tr("otherJobDescribeHelp")}
              </p>
              <textarea
                id="onboarding-other-desc"
                className="wpe-input min-h-[90px] resize-none text-[13px]"
                placeholder={tr("otherJobDescribePlaceholder")}
                value={otherDesc}
                onChange={(e) => setOtherDesc(e.target.value.slice(0, 600))}
                maxLength={600}
                data-testid="onboarding-other-desc-input"
              />
              <p className="text-[11px] text-[rgb(var(--wpe-muted))]">
                {tr("otherJobDescribeOptional")}
              </p>
            </div>
          )}
        </div>

        <PilotDisclaimer />

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="wpe-cta w-full"
            data-testid="onboarding-save-btn"
          >
            {submitting ? tr("processing") : tr("saveContinue")}
          </button>
          <button
            type="button"
            onClick={() => {
              setWork(null);
              setOtherDesc("");
              submit({ work_context: null, work_profile: null });
            }}
            className="wpe-ghost w-full text-[13px]"
            data-testid="onboarding-skip-btn"
          >
            {tr("skip")}
          </button>
        </div>
      </section>
    </AppShell>
  );
}

const LevelPick = ({ active, title, hint, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-lg p-3 border transition ${
      active
        ? "border-[rgb(var(--wpe-teal))] bg-[rgb(var(--wpe-teal-subtle))]"
        : "border-[rgb(var(--wpe-border))] bg-white"
    }`}
    data-testid={testid}
  >
    <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))]">{title}</div>
    <div className="text-[12px] text-[rgb(var(--wpe-muted))]">{hint}</div>
  </button>
);
